import { getUniqueColor } from "utils/helpers";
import {
  colorSchemeType,
  columnDataType,
  dateTypeEnum,
  numberTypeEnum,
  textTypeEnum,
} from "utils/types";

export class ColorConditionService {
  static parseStringCondition(condition, currentValue, uniqueColorsData) {
    let { type, inputsDataArray, color } = condition;

    const inputValue = inputsDataArray[0]?.toLowerCase().trim();
    currentValue = currentValue
      ? String(currentValue).toLowerCase().trim()
      : "";

    switch (type) {
      case textTypeEnum.uniqueValue: {
        return getUniqueColor(uniqueColorsData, currentValue);
      }
      case textTypeEnum.empty: {
        if (!currentValue) {
          return color;
        }
        return null;
      }
      case textTypeEnum.notEmpty: {
        if (currentValue) {
          return color;
        }
        return null;
      }
      case textTypeEnum.textContains: {
        if (currentValue.includes(inputValue) && inputValue !== "") {
          return color;
        }
        return null;
      }
      case textTypeEnum.textDoesNotContain: {
        if (!currentValue.includes(inputValue) && inputValue !== "") {
          return color;
        }
        return null;
      }
      case textTypeEnum.textStartsWith: {
        if (currentValue.startsWith(inputValue) && inputValue !== "") {
          return color;
        }
        return null;
      }
      case textTypeEnum.textEndsWith: {
        if (currentValue.endsWith(inputValue) && inputValue !== "") {
          return color;
        }
        return null;
      }
      case textTypeEnum.textIsExactly: {
        if (currentValue === inputValue && inputValue !== "") {
          return color;
        }
        return null;
      }
      default: {
        return null;
      }
    }
  }

  static parseNumberCondition(condition, currentValue, uniqueColorsData) {
    let { type, inputsDataArray, color } = condition;
    const isTypesForEmptyString = type === numberTypeEnum.uniqueValue || type === numberTypeEnum.empty || type === numberTypeEnum.not_empty

    if (!isTypesForEmptyString && currentValue === '') {
      return null
    }

    const inputValue = Number(inputsDataArray[0]);
    const secondInputValue = Number(inputsDataArray[1]);

    const fixedCurrentNumber = Number(currentValue).toFixed(2);
    currentValue = isTypesForEmptyString ? currentValue : Number(fixedCurrentNumber);

    switch (type) {
      case numberTypeEnum.uniqueValue: {
        return getUniqueColor(uniqueColorsData, currentValue);
      }
      case numberTypeEnum.empty: {
        if (currentValue === '') {
          return color;
        }
        return null;
      }
      case numberTypeEnum.notEmpty: {
        if (currentValue) {
          return color;
        }
        return null;
      }
      case numberTypeEnum.moreThan:
        if (currentValue > inputValue) {
          return color;
        }
        return null;
      case numberTypeEnum.moreThanOrEqualTo:
        if (currentValue >= inputValue) {
          return color;
        }
        return null;
      case numberTypeEnum.lessThan:
        if (currentValue < inputValue) {
          return color;
        }
        return null;
      case numberTypeEnum.lessThanOrEqualTo:
        if (currentValue <= inputValue) {
          return color;
        }
        return null;
      case numberTypeEnum.equal:
        if (currentValue === inputValue) {
          return color;
        }
        return null;
      case numberTypeEnum.notEqual:
        if (currentValue !== inputValue) {
          return color;
        }
        return null;
      case numberTypeEnum.isBetween:
        if (currentValue >= inputValue && currentValue <= secondInputValue) {
          return color;
        }
        return null;
      case numberTypeEnum.isNotBetween:
        if (currentValue < inputValue || currentValue > secondInputValue) {
          return color;
        }
        return null;
      default:
        return null;
    }
  }

  static parseDateCondition(condition, currentValue, uniqueColorsData) {
    let { type, inputsDataArray, color } = condition;
    const currentValueDate = new Date(currentValue);
    const inputDate = new Date(inputsDataArray[0]);
    const secondInputDate = new Date(inputsDataArray[1]);

    switch (type) {
      case dateTypeEnum.uniqueValue: {
        return getUniqueColor(uniqueColorsData, currentValue);
      }
      case dateTypeEnum.empty: {
        if (!currentValue) {
          return color;
        }
        return null;
      }
      case dateTypeEnum.notEmpty: {
        if (currentValue) {
          return color;
        }
        return null;
      }
      case dateTypeEnum.dateIs:
        if (currentValueDate?.getTime() === inputDate?.getTime()) {
          return color;
        }
        return null;
      case dateTypeEnum.dateIsAfter:
        if (inputDate < currentValueDate) {
          return color;
        }
        return null;
      case dateTypeEnum.dateIsBefore:
        if (inputDate > currentValueDate) {
          return color;
        }
        return null;
      case numberTypeEnum.isBetween:
        if (
          currentValueDate >= inputDate &&
          currentValueDate <= secondInputDate
        ) {
          return color;
        }
        return null;
      case numberTypeEnum.isNotBetween:
        if (
          currentValueDate < inputDate ||
          currentValueDate > secondInputDate
        ) {
          return color;
        }
        return null;
      default:
        return null;
    }
  }

  static getColorsForConditions({
    conditionsType,
    uniqueColorsData,
    conditions,
    currentValue,
  }) {
    const colors = [];

    if (
      conditionsType !== "" &&
      conditions?.length <= 0 &&
      currentValue === ""
    ) {
      return colors;
    }

    conditions?.forEach((condition) => {
      let currentColor = null;

      switch (conditionsType) {
        case columnDataType.text:
          currentColor = this.parseStringCondition(
            condition,
            currentValue,
            uniqueColorsData
          );
          break;
        case columnDataType.number:
          currentColor = this.parseNumberCondition(
            condition,
            currentValue,
            uniqueColorsData
          );
          break;
        case columnDataType.date:
          currentColor = this.parseDateCondition(
            condition,
            currentValue,
            uniqueColorsData
          );
          break;
        default:
          currentColor = null;
          break;
      }

      if (currentColor) {
        colors.push(currentColor);
      }
    });

    return colors;
  }

  static hexToRgb(hex) {
    const bigint = parseInt(hex.replace("#", ""), 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return { r: r, g: g, b: b };
  }

  static getGradientColor({ gradientColors, itemsCount, currentIndex }) {
    const minColorRgb = this.hexToRgb(gradientColors.firstValue);
    const maxColorRgb = this.hexToRgb(gradientColors.secondValue);
    const gradientRange = itemsCount ? itemsCount : 1;

    const gradientStep = {
      r: (minColorRgb.r - maxColorRgb.r) / gradientRange,
      g: (minColorRgb.g - maxColorRgb.g) / gradientRange,
      b: (minColorRgb.b - maxColorRgb.b) / gradientRange,
    };

    const currentColor = {
      r: Math.round(maxColorRgb.r + gradientStep.r * currentIndex),
      g: Math.round(maxColorRgb.g + gradientStep.g * currentIndex),
      b: Math.round(maxColorRgb.b + gradientStep.b * currentIndex),
    };

    return `rgb(${currentColor.r},${currentColor.g},${currentColor.b})`;
  }

  static getGradientColorFromArray({
    gradientColors,
    arrayForGradient,
    colorColumnKey,
    currentValue,
  }) {
    const uniqueArray = arrayForGradient.filter((item, index, arr) => {
      const lines = item.lines;
      let isNotUnique = true;
      for (let i = 0; i < index; i++) {
        if (lines[colorColumnKey] === arr[i].lines[colorColumnKey]) {
          isNotUnique = false;
        }
      }

      return isNotUnique;
    });

    uniqueArray.sort((a, b) => {
      const valueA = a.lines[colorColumnKey];
      const valueB = b.lines[colorColumnKey];
 
      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return valueB - valueA;
      }
      
      if (typeof valueA === 'string' || typeof valueB === 'string') {
        return valueA.localeCompare(valueB);
      }
      
      return 0;
    });

    const colorGradientPalette = {}

    uniqueArray.forEach((item, index) => {
      const currentValue = item.lines[colorColumnKey]

      const currentColor = this.getGradientColor({
        gradientColors,
        itemsCount: uniqueArray.length - 1,
        currentIndex: index,
      });
      colorGradientPalette[currentValue] = currentColor
    })

    return colorGradientPalette[currentValue] ?? 'black'
  }

  static getColorByColorScheme({
    colorScheme,
    uniqueColorsData,
    arrayForGradient,
    items,
  }) {
    const currentKey = colorScheme?.colorColumnKey;
    const currentValue = items[currentKey];
    const currentConditionType = colorScheme?.colorColumnType;
    const currentSchemeType = colorScheme?.colorSchemeType;
    const conditions = colorScheme?.colorSchemeConditionsData?.conditions;
    const gradientColors = colorScheme?.colorSchemeGradientData;

    switch (currentSchemeType) {
      case colorSchemeType.gradient:
        const color = this.getGradientColorFromArray({
          gradientColors: gradientColors,
          arrayForGradient: arrayForGradient,
          colorColumnKey: currentKey,
          currentKey: currentKey,
          currentValue: currentValue,
        });
        return {
          rowItemIndex: currentKey,
          color: color,
        };
      case colorSchemeType.conditionalColor:
        const resultColors = this.getColorsForConditions({
          conditions: conditions,
          currentValue: currentValue,
          uniqueColorsData: uniqueColorsData,
          conditionsType: currentConditionType,
        });

        if (resultColors?.length <= 0) {
          return null;
        }

        return {
          rowItemIndex: currentKey,
          color: resultColors[resultColors?.length - 1],
        };
      default:
        return {
          rowItemIndex: currentKey,
          color: null,
        };
    }
  }
}
